import { If } from '@shared/ui/if.component';
import { useCwL10n } from '@shared/use-cw-L10n.hook';
import React, { useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Container from 'typedi';
import { Urls } from '../../urls';
import css from './cookie-banner.component.less';
import { CookieConsentService } from './cookie-consent.service';
import { LoginWindow } from './log-in.component';

const layoutId = 'coreAppLoginPage';
const loginWindow = window as unknown as LoginWindow;

export function CookieBanner(): React.ReactElement {

    const cwL10n = useCwL10n();
    const cookieConsentService = Container.get(CookieConsentService);
    const [cookieConsentSetting, setCookieConsentSetting] = useState(cookieConsentService.getCookieConsentSetting() ?? (loginWindow.CwLogInPage.defaultDoNotTrack ? 'essential' : 'all'));
    const [displayBanner, setDisplayBanner] = useState(cookieConsentService.getCookieConsentSetting() === undefined);
    const [showCookieInfo, setShowCookieInfo] = useState(false);
    const [showCookieDetails, setShowCookieDetails] = useState(false);

    const cookiesUsed = [
        {
            name: 'TrimbleUnity_tenant',
            description: cwL10n.get('This cookie is used for authentication in our website.', 'text_label_cookieAuthDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: 'TrimbleUnity_reqtkn',
            description: cwL10n.get('This cookie is a part of security protection in our website.', 'text_label_cookieCSRFDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: 'TrimbleUnity_consent',
            description: cwL10n.get('This cookie is used to remember the cookie consent settings you have chosen on our website.', 'text_label_cookieConsentDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: 'tum_mfa_remember',
            description: cwL10n.get('This cookie is used for multi-factor authentication in our website.', 'text_label_cookieMFADesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: '*_CLIENT_SESSION_ID',
            description: cwL10n.get('These cookies are set by each of our applications to help cache application data for faster performance.', 'text_label_cookieDataSessionDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: 'AGS_ROLES',
            description: cwL10n.get('This is a Third Party cookie used for authentication for the interactive maps in the website.', 'text_label_cookieEsriDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: 'JSESSIONID',
            description: cwL10n.get('This is a Third Party cookie used for monitoring website performance and errors.', 'text_label_cookieNewRelicDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: 'apt.sid',
            description: cwL10n.get('This is a Third Party cookie used for tracking website usage.', 'text_label_cookieGainsightSidDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        },
        {
            name: 'apt.uid',
            description: cwL10n.get('This is a Third Party cookie used for tracking website usage.', 'text_label_cookieGainsightUidDesc', layoutId),
            status: cwL10n.get('Necessary', 'text_label_cookieStatusNecessary', layoutId),
        }
    ];

    function saveCookieConsentSetting(consentSetting?: 'all' | 'essential'): void {
        if (consentSetting) {
            setCookieConsentSetting(consentSetting);
            cookieConsentService.saveCookieConsentSetting(consentSetting);
        } else {
            setCookieConsentSetting(cookieConsentSetting ?? 'all');
            cookieConsentService.saveCookieConsentSetting(cookieConsentSetting ?? 'all');
        }
        setDisplayBanner(false);
        setShowCookieInfo(false);
        setShowCookieDetails(false);
    }

    const cookieDetailItems = cookiesUsed.map((cookie, i) => (
        <Accordion.Item eventKey={cookie.name} key={cookie.name}>
            <Accordion.Header>
                <strong>{cookie.name}</strong>
            </Accordion.Header>
            <Accordion.Body className={css.bodyText}>
                <dl>
                    <dt>{cwL10n.get('Category', 'text_label_cookieCategory', layoutId)}</dt>
                    <dd>{cookie.status}</dd>
                    <dt>{cwL10n.get('Description', 'text_label_cookieDescription', layoutId)}</dt>
                    <dd>{cookie.description}</dd>
                </dl>
            </Accordion.Body>
        </Accordion.Item>
    ));

    return (
        <>
            <If
                if={displayBanner}
                then={() =>
                    <div
                        role="dialog"
                        tabIndex={0}
                        aria-modal="true"
                        aria-label={cwL10n.get('Cookie Banner', 'text_label_cookieBanner', layoutId)}
                        className={css.cookieBanner}>
                        <div className={css.cookieText}>
                            {cwL10n.get('We use cookies to deliver and optimize the functionality on our websites. Some cookies are needed in order to give you the best possible experience. We respect your preferences regarding cookies on our websites.', 'text_label_cookieConsent', layoutId)}
                        </div>
                        <div className={css.cookieButtons}>
                            <Button
                                variant='secondary'
                                data-cw-id='btnShowCookieSettings'
                                onClick={() => setShowCookieInfo(true)}>
                                {cwL10n.get('Cookie Settings', 'ctl_btn_btnShowCookieSettings_buttonText', layoutId)}
                            </Button>
                            <Button
                                variant='primary'
                                data-cw-id='btnAcceptCookies'
                                onClick={() => saveCookieConsentSetting('all')}>
                                {cwL10n.get('I Accept', 'ctl_btn_btnAcceptCookies_buttonText', layoutId)}
                            </Button>
                        </div>
                    </div>
                }
            />
            <Modal
                show={showCookieInfo}
                onHide={() => {setShowCookieInfo(false); setShowCookieDetails(false);}}>
                <Modal.Header closeButton><img className={css.modalTitle} src='assets/logos/logo-blue.png' alt='' /></Modal.Header>
                <Modal.Body>
                    <If
                        if={showCookieDetails}
                        then={() =>
                            <>
                                <div>{ cwL10n.get('Cookie Details', 'ctl_modal_cookieDetails_header', layoutId) }</div>
                                <Button
                                    className={css.linkButton}
                                    variant='link'
                                    data-cw-id='btnHideCookieDetails'
                                    onClick={() => { setShowCookieDetails(false); }}>
                                    { cwL10n.get('Back', 'ctl_btn_btnHideCookieDetails_buttonText', layoutId) }
                                </Button>
                                <Accordion alwaysOpen>
                                    {cookieDetailItems}
                                </Accordion>
                            </>
                        }
                    />
                    <If
                        if={!showCookieDetails}
                        then={() =>
                            <>
                                <div className={css.titleText}>{ cwL10n.get('Do Not Track My Personal Data', 'text_header_cookieConsent', layoutId)}</div>
                                <div className={css.bodyText}>
                                    { cwL10n.get('When you visit our website, we store cookies on your browser to collect information. The information collected might relate to you, your preferences, or your device, and is mostly used to make the site work as you expect it to and to provide a more personalized web experience. However, you can choose not to allow certain types of cookies, which may impact your experience of the site and the services we are able to offer. Click on the different category headings to find out more and change our default settings according to your preference. You cannot opt-out of our First Party Strictly Necessary Cookies as they are deployed in order to ensure the proper functioning of our website (such as prompting the cookie banner and remembering your settings, to log into your account, site security, etc.). For more information about the First and Third Party Cookies used please follow this link.', 'text_body_cookieConsent', layoutId) }
                                </div>
                                <div>
                                    <Button
                                        className={css.linkButton}
                                        variant='link'
                                        data-cw-id='btnShowPrivacyNotice'
                                        href={Urls.privacyNoticeLink}
                                        target='_blank'
                                        rel='noopener noreferrer'>
                                        { cwL10n.get('Privacy Notice', 'ctl_btn_btnShowPrivacyNotice_buttonText', layoutId) }
                                    </Button>
                                </div>
                                <br/>
                                <Accordion defaultActiveKey="0">
                                    <Accordion.Item eventKey="0">
                                        <Accordion.Header>
                                            <div className={css.accordionHeader}>
                                                <div>{ cwL10n.get('Strictly Necessary Cookies', 'ctl_modal_strictlyNecessaryCookies_header', layoutId) }</div>
                                                <div className={css.alwaysActive}>{ cwL10n.get('Always Active', 'ctl_modal_alwaysActive_header', layoutId) }</div>
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <div className={css.bodyText}>
                                                { cwL10n.get('These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences, logging in, or filling out forms. You can set your browser to block or alert you about these cookies, but some parts of the site will not then work. These cookies do not store any personally identifiable information.', 'text_body_strictlyNecessaryCookies', layoutId) }
                                            </div>
                                            <div>
                                                <Button
                                                    className={css.linkButton}
                                                    variant='link'
                                                    data-cw-id='btnShowCookieDetails'
                                                    onClick={() => setShowCookieDetails(true)}>
                                                    { cwL10n.get('Cookie Details', 'ctl_btn_btnShowCookieDetails_buttonText', layoutId) }
                                                </Button>
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                    <Accordion.Item eventKey="1">
                                        <Accordion.Header>
                                            <div className={css.accordionHeader}>
                                                <div id="lblDoNotTrackMyPersonalData">{ cwL10n.get('Do Not Track My Personal Data', 'ctl_modal_doNotTrackMyPersonalData_header', layoutId) }</div>
                                                <Form.Switch
                                                    aria-labelledby="lblDoNotTrackMyPersonalData"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const checked = cookieConsentSetting !== 'essential';
                                                        setCookieConsentSetting(checked ? 'essential' : 'all');
                                                    }}
                                                    checked={cookieConsentSetting === 'essential'} />
                                            </div>
                                        </Accordion.Header>
                                        <Accordion.Body>
                                            <div className={css.bodyText}>
                                                { cwL10n.get('These cookies collect information for analytics and to personalize your experience on our website. These cookies may be set by us or by third party providers whose services we have added to our pages. You may exercise your right to opt out of sharing your personal information by using this toggle switch.', 'text_body_doNotTrackMyPersonalData', layoutId) }
                                            </div>
                                            <div>
                                                <Button
                                                    className={css.linkButton}
                                                    variant='link'
                                                    data-cw-id='btnShowCookieDetails2'
                                                    onClick={() => setShowCookieDetails(true)}>
                                                    { cwL10n.get('Cookie Details', 'ctl_btn_btnShowCookieDetails_buttonText', layoutId) }
                                                </Button>
                                            </div>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </>
                        }
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant='primary'
                        data-cw-id='btnConfirmCookieSettings'
                        onClick={() => saveCookieConsentSetting()}>
                        {cwL10n.get('Confirm My Choices', 'ctl_btn_btnConfirmCookieSettings_buttonText', layoutId)}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
